/**
 * Singleton binding seam for the active workspace id.
 *
 * The composition root (`main.tsx`) calls `configureWorkspaceContext()` once
 * at app boot, providing a `getWorkspaceId` function bound to the workspace
 * store. Lib-tier code (HTTP interceptors, axios clients) then calls
 * `getActiveWorkspaceId()` to read the id WITHOUT importing
 * `@/entities/workspace` directly.
 *
 * Why: lib must NOT depend on entities (FSD layer order). Without this
 * seam the workspace-id HTTP interceptor would create a `lib → entities`
 * edge — a real architectural inversion that would propagate any
 * workspace-state side effect into the transport layer.
 *
 * Pattern mirrors `entities/agent/lib/agent-metadata-bindings.ts` and
 * `entities/widget/lib/widget-store-bindings.ts`.
 */

export type WorkspaceContextBindings = {
  /** Returns the active workspace id (or `null` when none is selected). */
  getWorkspaceId: () => string | null;
};

let bindings: WorkspaceContextBindings | null = null;

/**
 * Configures the workspace-context bindings. Called once at app boot from
 * the composition root. Safe to call multiple times — subsequent calls
 * replace the bindings (used by tests via `configureWorkspaceContextForTests`).
 */
export const configureWorkspaceContext = (next: WorkspaceContextBindings): void => {
  bindings = next;
};

/**
 * Returns the current workspace id from the bound source. Returns `null`
 * when the binding is not configured (defensive default — lib code MUST
 * tolerate "not signed in / no workspace selected" without crashing the
 * HTTP layer).
 */
export const getActiveWorkspaceId = (): string | null => {
  if (bindings === null) return null;
  return bindings.getWorkspaceId();
};
